import { Injectable } from '@angular/core';
import { HttpClient, HttpRequest, HttpEvent } from '@angular/common/http';
import { Observable, of, catchError, retry, throwError, from } from 'rxjs';
import { map } from 'rxjs/operators';
import { switchMap } from 'rxjs/operators';
import  { ApiResponse }  from './data.service';
import { environment } from '../../environments/environment'; 
import { DataOfflineService } from './data-offline.service';
import { EnvironmentService } from './sync/environment.service';
import { SnackbarService } from './snackbar.service';
import { ErrorReportingService } from './error-reporting.service';
import { readFileForUpload } from '../_util/upload-file.util';

export function isAttachmentImage(attachment: { isImage?: boolean | null; mimeType?: string | null } | null | undefined): boolean {
  if (attachment?.isImage === true) {
    return true;
  }
  const mimeType = String(attachment?.mimeType ?? '').toLowerCase();
  return mimeType.startsWith('image/');
}

// const apiUrl = environment.apiUrl;

// export interface ApiResponse {
//   success: boolean;
//   message: string;
//   [key: string]: any; // Optional: allows additional payload (e.g., new file id)
// }

// export class KbApp_Attachment__DELETE_ME {
//   _id: string;
//   OwnerId: string;
//   FileName: string;
//   Size: number;       // Added 25.May.2025
//   Type: string;   // Obsolete?
//   UploadDatum: Date;
// }

export interface Attachment {
  _id: string; // unique ID (UUID or CouchDB _attachment name)
  filename: string; // original filename
  mimeType?: string; // MIME type (e.g. "image/png", "application/pdf")
  isImage?: boolean; // optional server hint; treat true as image
  imageWidth?: number; // optional intrinsic image width in px
  imageHeight?: number; // optional intrinsic image height in px
  naturalWidth?: number; // optional natural image width in px
  naturalHeight?: number; // optional natural image height in px
  fileSizeBytes?: number; // optional source payload size in bytes
  size: number; // file size in bytes
  userId: string; // owner (important for multi-user sync)
  created_on: string; // ISO timestamp
  modified_on?: string; // optional last-modified time
  checksum?: string; // optional (MD5/SHA1) to detect duplicates
  status: 'local' | 'uploading' | 'synced' | 'error' | 'deleted'; // sync state
  localUrl?: string; // blob URL or IndexedDB key for offline preview
  remoteUrl?: string; // REST-accessible URL for online access
  revision?: string; // CouchDB _rev of attachment if stored separately
  description?: string; // optional user-entered caption
}

@Injectable({
  providedIn: 'root',
})
export class FileUploadService {
  // Prefer env URL, but fall back to same-origin /api paths
  private readonly baseUrl = environment.apiBaseUrl || '/api';
  private attachmentsBaseUrl = `${this.baseUrl}/attachments`;
  private notesBaseUrl       = `${this.baseUrl}/notes`;

  constructor(
    private http: HttpClient,
    private offline: DataOfflineService,
    private environment: EnvironmentService,
    private snackbar: SnackbarService,
    private errorReporting: ErrorReportingService,
  ) {}

  // Best-effort caching of a blob into IndexedDB for offline use. The caller's action
  // (download/upload) has already succeeded by this point, so a failure here is reported
  // quietly rather than surfaced as if the action itself failed.
  private cacheAttachmentBlobBestEffort(
    payload: Parameters<DataOfflineService['saveAttachmentBlob']>[0],
  ): void {
    this.offline.saveAttachmentBlob(payload).catch((err) => {
      console.warn(`Failed to cache attachment "${payload.filename}" for offline use:`, err);
      this.errorReporting.report({
        message: `Failed to cache attachment for offline use: ${err?.message ?? err}`,
        error_type: err?.name ?? 'AttachmentCacheError',
        severity: 'warning',
        extra: { attachmentId: payload._id },
      });
      this.snackbar.show(`Saved "${payload.filename}", but couldn't cache it for offline use.`, 'warning');
    });
  }

  private normalizeAttachment(raw: any): Attachment {
    const id = String(raw?._id ?? raw?.id ?? raw?.attachmentId ?? '');
    const filename = String(
      raw?.filename ?? raw?.fileName ?? raw?.FileName ?? raw?.name ?? raw?.Name ?? id,
    );
    const createdOn = String(
      raw?.created_on ?? raw?.createdOn ?? raw?.createdAt ?? raw?.created ?? raw?.uploaded_on ?? raw?.UploadDatum ?? '',
    );
    const mimeType = String(raw?.mimeType ?? raw?.mimetype ?? raw?.type ?? raw?.Type ?? 'application/octet-stream');
    const size = Number(raw?.size ?? raw?.Size ?? 0);
    const imageWidthRaw = raw?.imageWidth;
    const imageHeightRaw = raw?.imageHeight;
    const parsedImageWidth = Number(imageWidthRaw);
    const parsedImageHeight = Number(imageHeightRaw);
    const naturalWidthRaw = raw?.naturalWidth ?? raw?.imageWidth;
    const naturalHeightRaw = raw?.naturalHeight ?? raw?.imageHeight;
    const parsedNaturalWidth = Number(naturalWidthRaw);
    const parsedNaturalHeight = Number(naturalHeightRaw);
    const fileSizeBytesRaw = raw?.fileSizeBytes ?? raw?.size ?? raw?.Size;
    const parsedFileSizeBytes = Number(fileSizeBytesRaw);
    const isImageRaw = raw?.isImage;

    return {
      ...(raw || {}),
      _id: id,
      filename,
      created_on: createdOn,
      mimeType,
      isImage: isImageRaw === true ? true : undefined,
      imageWidth: Number.isFinite(parsedImageWidth) && parsedImageWidth > 0 ? parsedImageWidth : undefined,
      imageHeight: Number.isFinite(parsedImageHeight) && parsedImageHeight > 0 ? parsedImageHeight : undefined,
      naturalWidth: Number.isFinite(parsedNaturalWidth) && parsedNaturalWidth > 0 ? parsedNaturalWidth : undefined,
      naturalHeight: Number.isFinite(parsedNaturalHeight) && parsedNaturalHeight > 0 ? parsedNaturalHeight : undefined,
      fileSizeBytes: Number.isFinite(parsedFileSizeBytes) && parsedFileSizeBytes > 0 ? parsedFileSizeBytes : undefined,
      size: Number.isFinite(size) ? size : 0,
      status: raw?.status ?? 'synced',
      userId: String(raw?.userId ?? raw?.OwnerId ?? ''),
    } as Attachment;
  }

  private extractAttachmentsPayload(resp: any): Attachment[] {
    if (Array.isArray(resp)) {
      return resp.map((item) => this.normalizeAttachment(item));
    }

    if (Array.isArray(resp?.data)) {
      return resp.data.map((item: any) => this.normalizeAttachment(item));
    }

    if (Array.isArray(resp?.data?.attachments)) {
      return resp.data.attachments.map((item: any) => this.normalizeAttachment(item));
    }

    if (Array.isArray(resp?.attachments)) {
      return resp.attachments.map((item: any) => this.normalizeAttachment(item));
    }

    if (Array.isArray(resp?.files)) {
      return resp.files.map((item: any) => this.normalizeAttachment(item));
    }

    return [];
  }

  // returns attachment list for given note record....  /api/notes/<_id>/attachments
getFiles(noteId: string, spaceId?: string): Observable<Attachment[]> {
  console.log('In getFiles()...');

  const encodedNoteId = encodeURIComponent(noteId);
  const spaceQuery = spaceId ? `&spaceId=${encodeURIComponent(spaceId)}` : '';
  const primaryUrl = `${this.notesBaseUrl}/${encodedNoteId}/attachments${spaceId ? `?spaceId=${encodeURIComponent(spaceId)}` : ''}`;
  const fallbackUrl = `${this.attachmentsBaseUrl}?noteId=${encodedNoteId}${spaceQuery}`;

  const tryFallback = () =>
    this.http.get<any>(fallbackUrl).pipe(
      map((resp) => this.extractAttachmentsPayload(resp)),
      catchError(this.handleError<Attachment[]>('getFiles (fallback)', [])),
    );

  const offlineFallback = () =>
    of(null).pipe(
      switchMap(async () => this.offline.getAttachmentMetasForNote(noteId)),
      map((rows) =>
        rows.map((row) =>
          this.normalizeAttachment({
            _id: row._id,
            filename: row.filename,
            size: row.size,
            mimeType: row.mimeType,
            isImage: row.isImage,
            imageWidth: row.imageWidth,
            imageHeight: row.imageHeight,
            naturalWidth: row.naturalWidth,
            naturalHeight: row.naturalHeight,
            fileSizeBytes: row.fileSizeBytes,
            status: row.hasBlob ? 'synced' : 'local',
            created_on: row.modified_on || row.cached_on || '',
          })
        )
      ),
      catchError(() => of([] as Attachment[])),
    );

  return this.http.get<any>(primaryUrl).pipe(
    map((resp) => this.extractAttachmentsPayload(resp)),
    switchMap((attachmentsFromPrimary) => {
      if (attachmentsFromPrimary.length > 0) {
        void this.cacheAttachmentMetas(noteId, attachmentsFromPrimary);
        return of(attachmentsFromPrimary);
      }
      // Primary URL returned empty — try fallback.
      return tryFallback();
    }),
    map((attachments) => {
      if (attachments.length > 0) {
        void this.cacheAttachmentMetas(noteId, attachments);
      }
      return attachments;
    }),
    switchMap((attachments) => {
      if (attachments.length > 0) {
        return of(attachments);
      }
      return offlineFallback();
    }),
    catchError(() => {
      // Primary URL failed with an HTTP error (e.g. 404) — try fallback.
      return tryFallback().pipe(
        switchMap((attachments) => {
          if (attachments.length > 0) {
            void this.cacheAttachmentMetas(noteId, attachments);
            return of(attachments);
          }
          return offlineFallback();
        })
      );
    }),
  );
}

  // returns a specific attachment given its id;  /api/attachments/<fileId>
  getFile(fileId: string, spaceId?: string): Observable<Blob> {
    return this.getFileForNote('', fileId, undefined, spaceId);
  }

  getFileForNote(noteId: string, fileId: string, fileName?: string, spaceId?: string): Observable<Blob> {
    const safeFileId = String(fileId ?? '').trim();
    const safeNoteId = String(noteId ?? '').trim();
    if (!safeFileId) {
      return throwError(() => new Error('Missing attachment id'));
    }

    const url = `${this.attachmentsBaseUrl}/${safeFileId}/file${spaceId ? `?spaceId=${encodeURIComponent(spaceId)}` : ''}`;

    return of(null).pipe(
      switchMap(async () => this.offline.getAttachmentBlob(safeFileId)),
      switchMap((cachedBlob) => {
        if (cachedBlob) {
          return of(cachedBlob);
        }

        return this.http.get(url, { responseType: 'blob' }).pipe(
          map((blob) => {
            const normalizedBlob = blob instanceof Blob ? blob : new Blob([blob]);
            this.cacheAttachmentBlobBestEffort({
              _id: safeFileId,
              noteId: safeNoteId,
              filename: fileName || safeFileId,
              mimeType: normalizedBlob.type || 'application/octet-stream',
              isImage: normalizedBlob.type.toLowerCase().startsWith('image/'),
              fileSizeBytes: normalizedBlob.size,
              size: normalizedBlob.size,
              blob: normalizedBlob,
            });
            return normalizedBlob;
          }),
        );
      }),
    );
  }

  private async cacheAttachmentMetas(noteId: string, attachments: Attachment[]): Promise<void> {
    const safeNoteId = String(noteId ?? '').trim();
    if (!safeNoteId || !Array.isArray(attachments)) {
      return;
    }

    const keepIds = attachments
      .map((item) => String(item?._id ?? '').trim())
      .filter((id) => !!id);

    await Promise.all(
      attachments.map((att) =>
        this.offline.saveAttachmentMeta({
          _id: String(att?._id ?? ''),
          noteId: safeNoteId,
          filename: String(att?.filename ?? att?._id ?? ''),
          mimeType: String(att?.mimeType ?? 'application/octet-stream'),
          isImage: att?.isImage,
          imageWidth: att?.imageWidth,
          imageHeight: att?.imageHeight,
          naturalWidth: att?.naturalWidth,
          naturalHeight: att?.naturalHeight,
          fileSizeBytes: att?.fileSizeBytes,
          size: Number(att?.size ?? 0),
          modified_on: att?.modified_on || att?.created_on || new Date().toISOString(),
        })
      )
    );

    await this.offline.pruneNoteAttachments(safeNoteId, keepIds);
  }

  // Best-effort metadata lookup used when a note stores only attachment IDs.
  getAttachmentInfo(attachmentId: string): Observable<Partial<Attachment> | null> {
    const url = `${this.attachmentsBaseUrl}/${attachmentId}`;

    return this.http.get<any>(url).pipe(
      map((resp) => {
        if (!resp) {
          return null;
        }

        if (resp?.status === 'success' && resp?.data) {
          return this.normalizeAttachment(resp.data);
        }

        if (resp?.data?.attachment) {
          return this.normalizeAttachment(resp.data.attachment);
        }

        if (resp?.attachment) {
          return this.normalizeAttachment(resp.attachment);
        }

        return this.normalizeAttachment(resp);
      }),
      switchMap(async (attachment) => {
        const normalized = attachment ? this.normalizeAttachment(attachment) : null;
        if (!normalized?._id) {
          return null;
        }

        if (!normalized.localUrl) {
          const cachedBlob = await this.offline.getAttachmentBlob(normalized._id);
          if (cachedBlob) {
            normalized.localUrl = this.createObjectUrl(cachedBlob);
          }
        }

        return normalized;
      }),
      catchError(() =>
        of(null).pipe(
          switchMap(async () => this.offline.getAttachmentMeta(attachmentId)),
          switchMap(async (meta) => {
            if (!meta) {
              return null;
            }

            const normalized = this.normalizeAttachment(meta);
            if (meta.hasBlob) {
              const cachedBlob = await this.offline.getAttachmentBlob(attachmentId);
              if (cachedBlob) {
                normalized.localUrl = this.createObjectUrl(cachedBlob);
              }
            }
            return normalized;
          }),
        ),
      ),
    );
  }

  // uploads an attachment to the server, takens NoteId to link it to
//   uploadAttachment(file: File, noteId: string): Observable<ApiResponse> {
//     const url = `${this.notesBaseUrl}/${noteId}/attachments`;
//     const formData = new FormData();
//     formData.append('file', file);

//     return this.http.post<ApiResponse>(url, formData).pipe(
//       retry(2),
//       map((res) => {
//         if (!res.success) {
//           throw new Error(res.message);
//         }
//         console.log('Uploaded Attachment (', file.name, ')');
//         return res;
//       }),
//       catchError(this.handleStandardError()),
//     );
//   }

/** True for the temporary id of an attachment queued by queueAttachmentUpload() (not on the server yet). */
static isQueuedAttachmentId(id: string | null | undefined): boolean {
  return String(id ?? '').startsWith('att-pending:');
}

/** Drops a queued attachment before the sync uploads it (the user removed it again). */
discardQueuedAttachment(id: string): void {
  if (!FileUploadService.isQueuedAttachmentId(id)) return;
  this.offline.deleteAttachment(id).catch((err) => console.warn(`Failed to discard queued attachment ${id}:`, err));
}

/** Fires when a queued attachment (see queueAttachmentUpload) was uploaded and got its server id. */
get attachmentIdReplaced$(): Observable<{ oldId: string; newId: string; noteId?: string }> {
  return this.offline.onAttachmentIdReplaced();
}

/** Stores the file in IndexedDB as a 'pending-upload' attachment under a temporary `att-pending:` id;
 * SyncCoordinatorService.runPushAttachments() uploads it later and swaps in the server's id. */
queueAttachmentUpload(file: File, noteId: string): Observable<ApiResponse<Attachment>> {
  const tempId = `att-pending:${(globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/^att-pending:/, '')}`;
  return from(
    this.offline.saveAttachmentBlob({
      _id: tempId,
      noteId,
      filename: file.name,
      mimeType: file.type || 'application/octet-stream',
      isImage: (file.type || '').toLowerCase().startsWith('image/'),
      fileSizeBytes: file.size,
      size: file.size,
      syncStatus: 'pending-upload',
      blob: file,
    })
  ).pipe(
    map(() => ({
      status: 'success' as const,
      data: {
        _id: tempId,
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
        size: file.size,
        userId: '',
        created_on: new Date().toISOString(),
        status: 'local' as const,
      } as Attachment,
    })),
    catchError((err) => {
      const message = err?.message ?? `Failed to save "${file.name}" for offline upload`;
      console.error('Attachment offline save error:', err);
      return of({
        status: 'error',
        error: { code: 500, message },
      } as ApiResponse<Attachment>);
    }),
  );
}

uploadAttachment(file: File, noteId: string, spaceId?: string): Observable<ApiResponse<Attachment>> {
  if (!this.environment.isOnline()) {
    return this.queueAttachmentUpload(file, noteId);
  }

  const spaceQuery = spaceId ? `&spaceId=${encodeURIComponent(spaceId)}` : '';
  const url = `${this.attachmentsBaseUrl}/upload?noteId=${encodeURIComponent(noteId)}${spaceQuery}`;

  // The bytes are read first (see readFileForUpload: iPhone photos), then posted.
  return from(readFileForUpload(file)).pipe(
    switchMap((uploadFile) => {
      const formData = new FormData();
      formData.append('file', uploadFile, uploadFile.name);
      return this.http.post<ApiResponse<Attachment>>(url, formData).pipe(retry(1));
    }),
    switchMap(async (res) => {
      // ✅ Log full server response
      console.debug('Upload response from Flask:', res);

      if (res.status !== 'success') {
        // If Flask returned an error object, extract details
        const serverMsg = res.error?.message ?? 'Unknown server error';
        const msg = `Upload failed for ${file.name}: ${serverMsg}`;
        console.warn('Attachment upload failed:', res);
        throw new Error(msg);
      }

      if (res.data?._id) {
        const normalized = this.normalizeAttachment(res.data);
        // Caching is best-effort: the upload already succeeded server-side, so a local
        // cache failure must not be allowed to make this look like a failed upload.
        this.cacheAttachmentBlobBestEffort({
          _id: normalized._id,
          noteId,
          filename: normalized.filename,
          mimeType: normalized.mimeType || file.type || 'application/octet-stream',
          isImage: normalized.isImage ?? isAttachmentImage(normalized),
          imageWidth: normalized.imageWidth,
          imageHeight: normalized.imageHeight,
          naturalWidth: normalized.naturalWidth,
          naturalHeight: normalized.naturalHeight,
          fileSizeBytes: normalized.fileSizeBytes ?? file.size,
          size: normalized.size || file.size,
          modified_on: normalized.modified_on || normalized.created_on || new Date().toISOString(),
          blob: file,
        });
      }

      console.log(`✅ Uploaded Attachment (${file.name})`);
      return res;
    }),
    catchError((err) => {
      // ✅ Handle both network and backend errors gracefully
      const message =
        err?.error?.error?.message ||
        err?.message ||
        `Unexpected upload error for ${file.name}`;
      console.error('Attachment upload error:', err);
      return of({
        status: 'error',
        error: { code: 500, message },
      } as ApiResponse<Attachment>);
    }),
  );
}

deleteAttachment(noteId: string, attachmentId: string, spaceId?: string): Observable<ApiResponse<null>> {
  const url = `${this.notesBaseUrl}/${noteId}/attachments/${attachmentId}${spaceId ? `?spaceId=${encodeURIComponent(spaceId)}` : ''}`;

  return this.http.delete<ApiResponse<null>>(url).pipe(
    retry(2),
    map((res) => {
      if (res.status !== 'success') {
        const msg = res.error?.message ?? 'Unknown delete error';
        throw new Error(msg);
      }
      console.log(`Deleted Attachment (ID=${attachmentId})`);
      return res;
    }),
    catchError(this.handleStandardError<null>()),
  );
}


renameAttachment(
  attachmentId: string,
  newName: string,
  noteId: string,
  spaceId?: string
): Observable<ApiResponse<Attachment>> {
  console.debug('## In file-upload-service.renameAttachment()');
  //const url = `${this.attachmentsBaseUrl}/api/attachments/${attachmentId}/rename`;
  const url = `${this.attachmentsBaseUrl}/${attachmentId}/rename`;
  const payload = { new_name: newName, KbItemId: noteId, spaceId };

  return this.http.post<ApiResponse<Attachment>>(url, payload).pipe(
    map((res) => {
      if (res.status !== 'success') {
        const msg = res.error?.message ?? 'Unknown rename error';
        throw new Error(msg);
      }
      console.log(`Renamed Attachment (new name = ${newName})`);
      return res;
    }),
    catchError(this.handleStandardError<Attachment>()),
  );
}

// TO DO : Test This!
copyAttachment(
  attachmentId: string,
  fromNoteId: string,
  toNoteId: string
): Observable<ApiResponse<Attachment>> {
  console.debug('## In file-upload-service.copyAttachment()');
  const url = `${this.attachmentsBaseUrl}/${attachmentId}/copy`;
  const payload = { noteId: fromNoteId, newNoteId: toNoteId };

  return this.http.post<ApiResponse<Attachment>>(url, payload).pipe(
    map((res) => {
      if (res.status !== 'success') {
        const msg = res.error?.message ?? 'Unknown copy error';
        throw new Error(msg);
      }
      console.log(`Copied Attachment (ID=${attachmentId})`);
      return res;
    }),
    catchError(this.handleStandardError<Attachment>()),
  );
}


private handleStandardError<T>() {
  return (error: any): Observable<ApiResponse<T>> => {
    let message = 'An unknown error occurred!';
    if (error?.error?.message) {
      message = error.error.message;
    } else if (error?.message) {
      message = error.message;
    }

    const response: ApiResponse<T> = {
      status: 'error',
      error: { code: 500, message },
    };

    return throwError(() => response);
  };
}


//   private handleStandardError() {
//     return (error: any) => {
//       let message = 'An unknown error occurred!';
//       if (error?.error?.message) {
//         message = error.error.message;
//       } else if (error?.message) {
//         message = error.message;
//       }
//       const response: ApiResponse = { status: 'error', message };
//       return throwError(() => response);
//     };
//   }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(error);
      this.log(`${operation} failed: ${error.message}`);

      return of(result as T);
    };
  }

  private log(message: string) {
    console.log(message);
  }

  private createObjectUrl(blob: Blob): string | undefined {
    if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
      return undefined;
    }

    // TODO: Revoke generated object URLs when editor/view consumers no longer need them.
    return URL.createObjectURL(blob);
  }
}
